import type { DayOfWeek } from '@gozoki/api-types'

export type ReapproComputeAlgorithm = 'VMAX' | 'VMJ' | 'STOCK_PROJETE' | 'STOCK_MINIMUM'
export type ReaproPeriode = '3J' | '5J' | '7J'

export interface ReapproRealtimeIndicators {
  stockActuel: number
  stockDLCCourte: number
  stockArrivant: number
  vma3j: number
  vma5j: number
  vma7j: number
  vjm3j: number
  vjm5j: number
  vjm7j: number
}

export const EMPTY_REALTIME_INDICATORS: ReapproRealtimeIndicators = {
  stockActuel: 0,
  stockDLCCourte: 0,
  stockArrivant: 0,
  vma3j: 0,
  vma5j: 0,
  vma7j: 0,
  vjm3j: 0,
  vjm5j: 0,
  vjm7j: 0,
}

export const sanitizeNumber = (value: unknown) => {
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

export const pickIndicatorByPeriode = (
  realtime: ReapproRealtimeIndicators | undefined,
  periode: ReaproPeriode,
): { vmaxBase: number; vmjBase: number } => {
  if (!realtime) {
    return { vmaxBase: 0, vmjBase: 0 }
  }
  if (periode === '3J') {
    return { vmaxBase: realtime.vma3j, vmjBase: realtime.vjm3j }
  }
  if (periode === '5J') {
    return { vmaxBase: realtime.vma5j, vmjBase: realtime.vjm5j }
  }
  return { vmaxBase: realtime.vma7j, vmjBase: realtime.vjm7j }
}

/**
 * Returns true if any of the coverage days (starting from deliveryDayOfWeek)
 * include a Saturday or Sunday.
 * @param deliveryDayOfWeek 0=Sunday, 1=Monday, ..., 6=Saturday (JS Date.getDay() convention)
 * @param coverageRate number of days to cover (ceiled to integer)
 */
export const getReelWeekEndCoef = (coveringDays: DayOfWeek[], initialCoef: number): number => {
  const isThereAWeekendInCoveringDays = coveringDays.some((day) =>
    ['saturday', 'sunday'].includes(day),
  )
  return isThereAWeekendInCoveringDays ? initialCoef : 1
}

interface ComputeProductNeedInput {
  algorithm: ReapproComputeAlgorithm
  coverageRate: number
  coefSecu: number
  coefWeekend: number
  stockMinimum: number | null
  stockActuel: number
  stockDLCCourte: number
  stockArrivant: number
  vmaxBase: number
  vmjBase: number
  daysBefore?: number
  /** 0=Sunday, 1=Monday, ..., 6=Saturday. When provided, coefWeekend only applies if coverage includes a weekend day. */
  deliveryDayOfWeek?: number
}

export interface ComputeProductNeedResult {
  besoin: number
  vmax: number
  vmj: number
  prevision: number
  stockProjete: number
  vmaxSubstitution: number
  vmjSubstitution: number
}

export const computeProductNeed = ({
  algorithm,
  coverageRate,
  coefSecu,
  coefWeekend,
  stockMinimum,
  stockActuel,
  stockDLCCourte,
  stockArrivant,
  vmaxBase,
  vmjBase,
  daysBefore = 0,
}: ComputeProductNeedInput): ComputeProductNeedResult => {
  const sa = sanitizeNumber(stockActuel)
  const dlc = sanitizeNumber(stockDLCCourte)
  const arr = sanitizeNumber(stockArrivant)
  const db = Math.max(0, sanitizeNumber(daysBefore))
  const tc = Math.max(0, sanitizeNumber(coverageRate))
  const cs = Math.max(0, sanitizeNumber(coefSecu))
  const rawCw = Math.max(0, sanitizeNumber(coefWeekend))
  const cw = rawCw ?? 1
  const vmaxSignal = Math.max(0, sanitizeNumber(vmaxBase))
  const vmjSignal = Math.max(0, sanitizeNumber(vmjBase))
  const stockProjete = Math.max(0, Math.ceil(sa - Math.max(vmjSignal * db, dlc) + arr))
  const prevision = vmjSignal * tc
  const vmaxSubstitution = vmaxSignal * tc - sa + dlc - arr
  const vmjSubstitution = vmjSignal * tc - sa + dlc - arr

  if (algorithm === 'STOCK_MINIMUM') {
    const customStockMin = sanitizeNumber(stockMinimum)
    const needFromCustomStockMin = customStockMin - (sa - dlc)
    return {
      besoin: Math.max(0, Math.round(needFromCustomStockMin)),
      vmax: vmaxSignal,
      vmj: vmjSignal,
      prevision,
      stockProjete,
      vmaxSubstitution,
      vmjSubstitution,
    }
  }

  if (algorithm === 'STOCK_PROJETE') {
    return {
      besoin: Math.max(0, Math.round(vmaxSignal * cs * tc - stockProjete)),
      vmax: vmaxSignal,
      vmj: vmjSignal,
      prevision,
      stockProjete,
      vmaxSubstitution,
      vmjSubstitution,
    }
  }

  if (algorithm === 'VMAX') {
    return {
      besoin: Math.max(0, Math.round(vmaxSubstitution * cs * cw)),
      vmax: vmaxSignal,
      vmj: vmjSignal,
      prevision,
      stockProjete,
      vmaxSubstitution,
      vmjSubstitution,
    }
  }

  return {
    besoin: Math.max(0, Math.round(vmjSubstitution * cs * cw)),
    vmax: vmaxSignal,
    vmj: vmjSignal,
    prevision,
    stockProjete,
    vmaxSubstitution,
    vmjSubstitution,
  }
}
